import GeoJSON from "geojson";
import { toast } from "@iventis/toasts/src/toast";
import { Slice } from "@reduxjs/toolkit";
import bbox from "@turf/bbox";
import bboxPolygon from "@turf/bbox-polygon";
import { UseIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Content } from "@iventis/translations/content/typed-content";
import { DataFieldType } from "@iventis/domain-model/model/dataFieldType";
import { MapObjectRepeatedTimeRangeValue } from "@iventis/types";
import { DomainLayer } from "@iventis/map-engine/src/state/map.state.types";
import { isMapObjectWithinSingleRepeatedTimeRange } from "@iventis/map-engine";
import { isAdmin } from "@iventis/utilities";
import { Authorisation } from "@iventis/domain-model/model/authorisation";
import { RootDispatch, rootStore } from "./state/root.store";
import { patchMapLayers } from "./state/map.slice.thunks";

export const panToObject = async (objectFeature: GeoJSON.Feature | GeoJSON.FeatureCollection, dispatch: RootDispatch, map: Slice) => {
    const boundingBox = bbox(objectFeature);
    // Convert the bounding box to a polygon as that is how we store bounds
    const polygon = bboxPolygon(boundingBox);
    // Zoom to the object
    dispatch(map.actions.setBounds(polygon.geometry.coordinates));
};

export const isClickedFeatureFilteredByDates = (layer: DomainLayer, objectFeature: GeoJSON.Feature, dayFilter: number, timeFilter: number) => {
    const repeatedTimeRangeDataFields = layer.dataFields.filter((df) => df.type === DataFieldType.RepeatedTimeRanges);

    // If there are no repeated time range data fields, the feature is not filtered by dates
    if (repeatedTimeRangeDataFields.length === 0) {
        return false;
    }
    // Get all repeated time range values for the object
    const mapObjectTimeRangeValues = repeatedTimeRangeDataFields.reduce<MapObjectRepeatedTimeRangeValue[]>((mapObjectTimeRanges, timeRangeDataField) => {
        if (objectFeature.properties[timeRangeDataField.id] != null) {
            mapObjectTimeRanges.push(...(objectFeature.properties[timeRangeDataField.id] as MapObjectRepeatedTimeRangeValue[]));
        }
        return mapObjectTimeRanges;
    }, []);

    // Map object has no repeated time range values and therefore is not filtered by dates
    if (mapObjectTimeRangeValues.length === 0) {
        return false;
    }

    // Check if the object is within a single repeated time range
    return !isMapObjectWithinSingleRepeatedTimeRange(mapObjectTimeRangeValues, dayFilter, timeFilter);
};

export const handleSelectedMapObject = async (
    layer: DomainLayer & { authorisation: Authorisation },
    objectFeature: GeoJSON.Feature,
    currentMapObjectLevel: number,
    level: number,
    dispatch: RootDispatch,
    map: Slice,
    translate: UseIventisTranslate,
    datesFilter: { filter: boolean; day?: number; time?: number },
    selectMapObject?: () => void
) => {
    if (layer.visible === false && currentMapObjectLevel !== level) {
        toast.info({
            testId: "map-object-not-visible-and-on-different-level-toast",
            button: { label: translate(Content.map8.mapObjectSelectionHelpers.layerNotVisibleAndObjectOnDifferentLevel.cta) },
            onClick: async () => {
                if (isAdmin(layer.authorisation)) {
                    rootStore.dispatch(patchMapLayers([{ id: layer.id, visible: true }]));
                } else {
                    dispatch(map.actions.updateMapLayer({ ...layer, visible: true }));
                }
                dispatch(map.actions.toggleDateFilter(false));
                dispatch(map.actions.updateSelectedLevel(currentMapObjectLevel));
                selectMapObject?.();
                await panToObject(objectFeature, dispatch, map);
            },
            message: translate(Content.map8.mapObjectSelectionHelpers.layerNotVisibleAndObjectOnDifferentLevel.message),
            title: translate(Content.common.alert),
        });
    } else if (layer.visible === false) {
        toast.info({
            testId: "map-object-not-visible-toast",
            button: { label: translate(Content.map8.mapObjectSelectionHelpers.layerNotVisible.cta) },
            onClick: async () => {
                if (isAdmin(layer.authorisation)) {
                    rootStore.dispatch(patchMapLayers([{ id: layer.id, visible: true }]));
                } else {
                    dispatch(map.actions.updateMapLayer({ ...layer, visible: true }));
                }
                dispatch(map.actions.toggleDateFilter(false));
                selectMapObject?.();
                await panToObject(objectFeature, dispatch, map);
            },
            message: translate(Content.map8.mapObjectSelectionHelpers.layerNotVisible.message),
            title: translate(Content.common.alert),
        });
    } else if (currentMapObjectLevel !== level) {
        toast.info({
            testId: "map-object-on-different-level-toast",
            button: { label: translate(Content.map10.switchLevel) },
            onClick: async () => {
                dispatch(map.actions.updateSelectedLevel(currentMapObjectLevel));
                dispatch(map.actions.toggleDateFilter(false));
                selectMapObject?.();
                await panToObject(objectFeature, dispatch, map);
            },
            message: translate(Content.map8.mapObjectSelectionHelpers.objectOnDifferentLevel.message),
            title: translate(Content.common.alert),
        });
    } else if (datesFilter.filter && isClickedFeatureFilteredByDates(layer, objectFeature, datesFilter.day, datesFilter.time)) {
        toast.info({
            testId: "map-object-filtered-by-date",
            button: { label: translate(Content.map10.removeFilter) },
            onClick: async () => {
                dispatch(map.actions.toggleDateFilter(false));
                selectMapObject?.();
                await panToObject(objectFeature, dispatch, map);
            },
            message: translate(Content.map8.dates.mapObjectDateFiltered),
            title: translate(Content.common.alert),
        });
    } else {
        panToObject(objectFeature, dispatch, map);
        selectMapObject?.();
    }
};
